// LT-511: success state — confirmation card with When / Host / Timezone
// / Meeting rows. Pulses the check icon once on mount.
import React from 'react';
import { Calendar as CalendarIcon, Check, Globe, User, Video } from 'lucide-react';
import { Button, Card } from '../../../ui';
import { endTime, formatDayHeading, slotTime } from '../formatters';

interface Props {
  date: string;
  slotIso: string;
  timezone: string;
  durationMinutes: number;
  ownerName?: string | null;
  locationLabel?: string | null;
  email: string;
  onReset: () => void;
}

interface RowProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}
const Row: React.FC<RowProps> = ({ icon, label, children }) => (
  <div className="flex gap-3">
    <div className="text-slate-500 mt-0.5" aria-hidden="true">{icon}</div>
    <div className="flex-1">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-slate-300">{children}</div>
    </div>
  </div>
);

const BookingSuccess: React.FC<Props> = ({
  date, slotIso, timezone, durationMinutes,
  ownerName, locationLabel, email, onReset,
}) => (
  <div
    className="flex flex-col items-center text-center px-4 py-8 bp-fade-up"
    data-testid="public-booking-success"
  >
    <div className="w-14 h-14 rounded-full bg-ok-tint border border-ok-border text-ok-text flex items-center justify-center mb-5 bp-pulse-ok">
      <Check className="w-6 h-6" />
    </div>
    <h2 className="m-0 text-xl font-semibold text-slate-50">You're booked.</h2>
    <p className="mt-2 mb-5 text-sm text-slate-400 max-w-[360px]">
      We sent a confirmation with the meeting details to{' '}
      <span className="text-slate-300">{email}</span>.
    </p>
    <Card padding="default" className="w-full max-w-[360px] text-left flex flex-col gap-2.5">
      <Row icon={<CalendarIcon className="w-3.5 h-3.5" />} label="When">
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatDayHeading(date)} · {slotTime(slotIso, timezone)} – {endTime(slotIso, durationMinutes, timezone)}
        </span>
      </Row>
      {ownerName && (
        <Row icon={<User className="w-3.5 h-3.5" />} label="Host">{ownerName}</Row>
      )}
      <Row icon={<Globe className="w-3.5 h-3.5" />} label="Timezone">
        <span className="font-mono text-xs">{timezone}</span>
      </Row>
      <Row icon={<Video className="w-3.5 h-3.5" />} label="Meeting">
        {locationLabel ?? 'Meeting link sent to your email'}
      </Row>
    </Card>
    <Button intent="ghost" onClick={onReset} className="mt-6">
      Book another time
    </Button>
  </div>
);

export default BookingSuccess;
